import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  FolderKanban, 
  Search, 
  Trash2, 
  Edit, 
  Building2,
  CheckCircle,
  XCircle,
  Layers,
  Home,
  PlusCircle,
  X,
  Shield,
  Power
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { projectsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useSuperUser } from '../context/SuperUserContext';
import { SuperUserDialog } from '../components/SuperUserDialog';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Super User states
  const { isSuperUser } = useSuperUser();
  const [superUserDialogOpen, setSuperUserDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    animal_type: '',
    description: '',
    naves: [1],
    lotes: [], // Array de { numero: '1', naves: [1, 2] }
    status: 'activo'
  });
  
  // Para seleccionar proyecto existente o crear nuevo
  const [useExistingProject, setUseExistingProject] = useState(false);
  const [selectedExistingProject, setSelectedExistingProject] = useState('');
  
  // Obtener lista de nombres únicos de proyectos
  const uniqueProjectNames = [...new Set(projects.map(p => p.name))];
  
  const ANIMAL_TYPES = [
    { value: 'pollo', label: 'Pollo' },
    { value: 'gallina', label: 'Gallina' },
    { value: 'cerdo', label: 'Cerdo' },
    { value: 'becerro', label: 'Becerro' }
  ];
  
  // Para agregar lote
  const [newLoteNumber, setNewLoteNumber] = useState('');
  const [newLoteNaves, setNewLoteNaves] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', animal_type: '', description: '', naves: [1], lotes: [], status: 'activo' });
    setNewLoteNumber('');
    setNewLoteNaves('');
    setEditingProject(null);
    setUseExistingProject(false);
    setSelectedExistingProject('');
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      // Si es edición, verificar super usuario
      if (!isSuperUser) {
        setPendingAction({ type: 'edit', data: project });
        setSuperUserDialogOpen(true);
        return;
      }
      executeOpenDialog(project);
    } else {
      resetForm();
      setDialogOpen(true);
    }
  };

  const executeOpenDialog = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      animal_type: project.animal_type || '',
      description: project.description || '',
      naves: project.naves || [1],
      lotes: project.lotes || [],
      status: project.status
    });
    setDialogOpen(true);
  };

  const parseNaves = (input) => {
    // Parse comma-separated numbers
    const naves = input
      .split(',')
      .map(s => parseInt(s.trim()))
      .filter(n => !isNaN(n) && n > 0);
    return naves.length > 0 ? naves : [1];
  };

  // Agregar un lote con sus naves
  const handleAddLote = () => {
    if (!newLoteNumber.trim()) {
      toast.error('Ingresa el número de lote');
      return;
    }
    if (!newLoteNaves.trim()) {
      toast.error('Ingresa al menos una nave');
      return;
    }
    
    const naves = parseNaves(newLoteNaves);
    const newLote = {
      number: newLoteNumber.trim(),  // Usar 'number' para coincidir con el backend
      naves: naves
    };
    
    // Verificar si el lote ya existe
    if (formData.lotes.some(l => l.number === newLote.number)) {
      toast.error(`El lote ${newLote.number} ya existe`);
      return;
    }
    
    // Actualizar naves totales del proyecto (unión de todas las naves)
    const allNaves = [...new Set([...formData.naves, ...naves])].sort((a, b) => a - b);
    
    setFormData({
      ...formData,
      lotes: [...formData.lotes, newLote],
      naves: allNaves
    });
    
    setNewLoteNumber('');
    setNewLoteNaves('');
    toast.success(`Lote ${newLote.number} agregado con Nave(s) ${naves.join(', ')}`);
  };

  // Eliminar un lote
  const handleRemoveLote = (index) => {
    const newLotes = formData.lotes.filter((_, i) => i !== index);
    // Recalcular naves totales
    const allNaves = newLotes.length > 0 
      ? [...new Set(newLotes.flatMap(l => l.naves))].sort((a, b) => a - b)
      : [1];
    setFormData({ ...formData, lotes: newLotes, naves: allNaves });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Ingresa el nombre del proyecto');
      return;
    }
    
    // Calcular naves totales desde los lotes
    const allNaves = formData.lotes.length > 0 
      ? [...new Set(formData.lotes.flatMap(l => l.naves))].sort((a, b) => a - b)
      : formData.naves;
    
    const submitData = { ...formData, naves: allNaves };
    
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, submitData);
        toast.success('Proyecto actualizado');
      } else {
        await projectsAPI.create(submitData);
        toast.success('Proyecto creado');
      }
      resetForm();
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proyecto');
    }
  };

  const handleDelete = async (id) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'delete', data: id });
      setSuperUserDialogOpen(true);
      return;
    }
    executeDelete(id);
  };

  const executeDelete = async (id) => {
    try {
      await projectsAPI.delete(id);
      toast.success('Proyecto eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Función para desactivar/activar proyecto (cambiar estado)
  const handleToggleStatus = async (project) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'toggle', data: project });
      setSuperUserDialogOpen(true);
      return;
    }
    executeToggleStatus(project);
  };

  const executeToggleStatus = async (project) => {
    try {
      const newStatus = project.status === 'activo' ? 'inactivo' : 'activo';
      await projectsAPI.update(project.id, { status: newStatus });
      toast.success(`Proyecto ${newStatus === 'activo' ? 'activado' : 'desactivado'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleSuperUserVerified = () => {
    if (!pendingAction) return;
    switch (pendingAction.type) {
      case 'edit':
        executeOpenDialog(pendingAction.data);
        break;
      case 'delete':
        executeDelete(pendingAction.data);
        break;
      case 'toggle':
        executeToggleStatus(pendingAction.data);
        break;
      default:
        break;
    }
    setPendingAction(null);
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Stats
  const activeProjects = projects.filter(p => p.status === 'activo').length;
  const completedProjects = projects.filter(p => p.status === 'completado').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Proyectos</h1>
          <p className="text-slate-500 mt-1">Gestión de proyectos para despachos internos</p>
        </div>
        <Button 
          className="bg-[#931A25] hover:bg-[#7A151F]"
          onClick={() => handleOpenDialog()}
          data-testid="add-project-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Proyecto
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#931A25]/10 flex items-center justify-center">
                <FolderKanban className="h-5 w-5 text-[#931A25]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Proyectos</p>
                <p className="font-bold text-lg">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Activos</p>
                <p className="font-bold text-lg text-green-600">{activeProjects}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Completados</p>
                <p className="font-bold text-lg text-blue-600">{completedProjects}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar proyecto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-projects"
            />
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle>Lista de Proyectos</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nombre</TableHead>
                <TableHead>Animal</TableHead>
                <TableHead className="text-center">Lotes #</TableHead>
                <TableHead className="text-center">Naves</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead>Creado</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No hay proyectos registrados
                  </TableCell>
                </TableRow>
              ) : (
                filteredProjects.map((project) => {
                  const loteCount = project.lotes?.length || 0;
                  const animalColors = {
                    'pollo': 'bg-red-100 text-red-800',
                    'gallina': 'bg-green-100 text-green-800',
                    'cerdo': 'bg-pink-100 text-pink-800',
                    'becerro': 'bg-amber-100 text-amber-800'
                  };
                  return (
                  <TableRow key={project.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{project.name}</TableCell>
                    <TableCell>
                      {project.animal_type ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${animalColors[project.animal_type] || 'bg-blue-100 text-blue-800'}`}>
                          {project.animal_type === 'pollo' ? 'Pollo' : 
                           project.animal_type === 'gallina' ? 'Gallina' : 
                           project.animal_type === 'cerdo' ? 'Cerdo' : 
                           project.animal_type === 'becerro' ? 'Becerro' : project.animal_type}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {project.lotes && project.lotes.length > 0 ? (
                          project.lotes.map((lote, idx) => (
                            <span 
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium"
                            >
                              Lote {lote.number || lote}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {project.naves?.map(nave => (
                          <span 
                            key={nave}
                            className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium"
                          >
                            Nave {nave}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        project.status === 'activo' 
                          ? 'bg-green-100 text-green-700' 
                          : project.status === 'inactivo'
                          ? 'bg-red-100 text-red-700'
                          : project.status === 'completado'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {project.status === 'activo' ? 'Activo' : 
                         project.status === 'inactivo' ? 'Inactivo' :
                         project.status === 'completado' ? 'Completado' : 'Cancelado'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(project.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleStatus(project)}
                          className={`hover:bg-slate-100 ${project.status === 'activo' ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-slate-500'}`}
                          title={project.status === 'activo' ? 'Desactivar lote' : 'Activar lote'}
                        >
                          <Power className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog(project)}
                          className={`hover:bg-blue-50 ${isSuperUser ? 'text-blue-600 hover:text-blue-700' : 'text-slate-400 hover:text-slate-500'}`}
                          title={isSuperUser ? 'Editar' : 'Requiere Super Usuario'}
                        >
                          <Edit className="h-4 w-4" />
                          {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(project.id)}
                          className={`hover:bg-red-50 ${isSuperUser ? 'text-red-600 hover:text-red-700' : 'text-slate-400 hover:text-slate-500'}`}
                          title={isSuperUser ? 'Eliminar' : 'Requiere Super Usuario'}
                        >
                          <Trash2 className="h-4 w-4" />
                          {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FolderKanban className="h-6 w-6 text-[#931A25]" />
              {editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
            </DialogTitle>
            <DialogDescription>
              Configura el proyecto, sus lotes y las naves asignadas a cada lote.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre del Proyecto */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-base font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Nombre del Proyecto *
              </Label>
              
              {!editingProject && uniqueProjectNames.length > 0 && (
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      checked={!useExistingProject}
                      onChange={() => { setUseExistingProject(false); setSelectedExistingProject(''); setFormData({...formData, name: ''}); }}
                      className="w-4 h-4 text-[#931A25]"
                    />
                    <span className="text-sm">Crear nuevo proyecto</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      checked={useExistingProject}
                      onChange={() => setUseExistingProject(true)}
                      className="w-4 h-4 text-[#931A25]"
                    />
                    <span className="text-sm">Seleccionar proyecto existente</span>
                  </label>
                </div>
              )}
              
              {useExistingProject && !editingProject ? (
                <Select 
                  value={selectedExistingProject || "none"} 
                  onValueChange={(v) => {
                    setSelectedExistingProject(v);
                    if (v !== "none") {
                      // Buscar el proyecto y obtener su animal_type
                      const existingProj = projects.find(p => p.name === v);
                      setFormData({
                        ...formData, 
                        name: v,
                        animal_type: existingProj?.animal_type || formData.animal_type
                      });
                    }
                  }}
                >
                  <SelectTrigger className="text-lg py-5" data-testid="project-name-select">
                    <SelectValue placeholder="Seleccionar proyecto existente..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar proyecto...</SelectItem>
                    {uniqueProjectNames.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Carlos Lantigua, Granja Norte"
                  className="text-lg py-5"
                  required
                  data-testid="project-name-input"
                />
              )}
              
              {useExistingProject && selectedExistingProject && (
                <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Se creará un nuevo lote para el proyecto "{selectedExistingProject}"
                </p>
              )}
            </div>
            
            {/* Tipo de Animal */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Animal *</Label>
              <Select 
                value={formData.animal_type || "none"} 
                onValueChange={(v) => setFormData({...formData, animal_type: v === "none" ? "" : v})}
              >
                <SelectTrigger data-testid="project-animal-select">
                  <SelectValue placeholder="Seleccionar animal..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleccionar animal...</SelectItem>
                  {ANIMAL_TYPES.map((animal) => (
                    <SelectItem key={animal.value} value={animal.value}>{animal.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descripción del proyecto..."
                rows={2}
              />
            </div>

            {/* Sección de Lotes y Naves */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Lotes y Naves Asignadas
                </Label>
              </div>
              
              {/* Panel para agregar lote */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-sm text-slate-600 mb-3">Agrega los lotes del proyecto y asigna las naves correspondientes a cada uno.</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="lote-number" className="text-xs text-slate-500">Número de Lote</Label>
                    <Input
                      id="lote-number"
                      value={newLoteNumber}
                      onChange={(e) => setNewLoteNumber(e.target.value)}
                      placeholder="Ej: 1, 2-A, 3"
                      data-testid="new-lote-number"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="lote-naves" className="text-xs text-slate-500">Naves del Lote</Label>
                    <Input
                      id="lote-naves"
                      value={newLoteNaves}
                      onChange={(e) => setNewLoteNaves(e.target.value)}
                      placeholder="Ej: 1, 2, 3"
                      data-testid="new-lote-naves"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      type="button" 
                      onClick={handleAddLote}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Agregar Lote
                    </Button>
                  </div>
                </div>
              </div>

              {/* Lista de lotes agregados */}
              {formData.lotes.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Lotes configurados:</p>
                  <div className="grid gap-2">
                    {formData.lotes.map((lote, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <Layers className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">Lote {lote.number}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {lote.naves.map(nave => (
                                <span 
                                  key={nave}
                                  className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium"
                                >
                                  <Home className="h-3 w-3 mr-1" />
                                  Nave {nave}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveLote(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <Layers className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No hay lotes configurados</p>
                  <p className="text-xs text-slate-400">Agrega lotes usando el formulario de arriba</p>
                </div>
              )}

              {/* Resumen de naves totales */}
              {formData.lotes.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Naves totales del proyecto: 
                    <span className="font-bold">
                      {[...new Set(formData.lotes.flatMap(l => l.naves))].sort((a, b) => a - b).join(', ')}
                    </span>
                  </p>
                </div>
              )}
            </div>
            
            {/* Estado */}
            <div className="space-y-2">
              <Label>Estado del Proyecto</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({...formData, status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      Activo
                    </span>
                  </SelectItem>
                  <SelectItem value="completado">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      Completado
                    </span>
                  </SelectItem>
                  <SelectItem value="cancelado">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                      Cancelado
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="gap-2 pt-4 border-t">
              <DialogClose asChild>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
              </DialogClose>
              <Button 
                type="submit" 
                className="bg-[#931A25] hover:bg-[#7A151F]" 
                data-testid="save-project-btn"
                disabled={formData.lotes.length === 0 && !editingProject}
              >
                {editingProject ? 'Actualizar Proyecto' : 'Crear Proyecto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Super User Verification Dialog */}
      <SuperUserDialog
        open={superUserDialogOpen}
        onOpenChange={setSuperUserDialogOpen}
        onSuccess={handleSuperUserVerified}
        title="Acción Restringida"
        description="Esta acción requiere verificación de super usuario."
      />
    </div>
  );
}
